import { ReactNode, MouseEvent, useState } from 'react';
import Link from 'next/link';
import { Post } from 'lib/types';
import { buildPostPath } from 'lib/helpers';
import { PostAccount } from 'components/PostAccount';
import { PostMetrics } from 'components/PostMetrics';
import { CreditsPreview } from 'components/CreditsPreview';
import { trackClickPostCardEvent, trackClickAddFavoritePost, trackClickSignupEvent } from 'lib/hooks/useEventTracking';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useApi } from 'lib/contexts/ApplicationState';
import { PostCardClickContext } from 'lib/types/events';
import StarIcon from '@mui/icons-material/Star';
import StarTwoToneIcon from '@mui/icons-material/StarTwoTone';
import { MobileTooltip } from 'components/MobileTooltip';
import { useSnackbar } from 'notistack';
import { useSignUpModal } from 'components/SignUpModal';
import { PostCardMenu } from './PostCardMenu';
import { PostCardMedia } from './PostCardMedia';
import { PinPost } from './PinPost';
import { MatchHeader } from './MatchHeader';
import { MatchMetrics } from './MatchMetrics';

export interface Props {
  post: Post;
  isProfileOwner?: boolean;
  showPin?: boolean;
  fetchData?: () => void;
  showMatchMetrics?: boolean;
  mediaClassName?: string;
  showFavorite?: boolean;
  /** The context is used with event tracking to keep track of where the card was clicked */
  context?: PostCardClickContext;
  upperRightOverlay?: ReactNode;
  /** Use fixedSize to force the media to crop to a certain size */
  fixedSize?: { width: number; height: number };
  /** Callback to ignore a suggested credit. This will simply hide the post from the parent state */
  onIgnoreSuggestedCredit?: (postId: number) => void;
  /** If true, the links in the post card will not be clickable */
  disableLinks?: boolean;
  projectListId?: number;
  projectId?: number;
  greyOutPosts?: boolean;
}

export const PostCard = ({
  post,
  isProfileOwner,
  showPin,
  fetchData,
  mediaClassName,
  context,
  upperRightOverlay,
  fixedSize,
  onIgnoreSuggestedCredit,
  disableLinks = false,
  showMatchMetrics,
  showFavorite,
  projectId,
  projectListId,
  greyOutPosts,
}: Props) => {
  const postDetailHref = buildPostPath(post);
  const API = useApi();
  const [isFavorited, setIsFavorited] = useState(post.isFavorited);
  const { enqueueSnackbar } = useSnackbar();
  const { showSignUpModal } = useSignUpModal();

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (disableLinks) {
      e.preventDefault();
      return;
    }
    trackClickPostCardEvent(API, post.id, context);
  };

  const handleFavoritePost = async () => {
    if (!isFavorited) {
      await trackClickAddFavoritePost(API, post.id, 'post-card');
    }

    if (!post.favoritesListId) {
      trackClickSignupEvent(API, 'post-card-add-favorite');
      showSignUpModal('Sign in to save your favorite posts!');
      return;
    }

    try {
      if (isFavorited) {
        await API.removeItemFromList(post.favoritesListId, post.id);
      } else {
        await API.addPostToList(post.favoritesListId, post.id);
      }
      setIsFavorited(!isFavorited);
    } catch (err) {
      enqueueSnackbar('An error occurred while trying to favorite this post');
    }
  };

  const removePostFromProject = async () => {
    if (!projectListId) {
      return;
    }
    try {
      await API.removePostFromProject(projectId!, post.id);
      fetchData?.();
    } catch (err) {
      enqueueSnackbar('An error occurred while trying to remove this post from the project');
    }
  };

  const greyOutCard = !post.credits.length && context === 'profile' && greyOutPosts;

  return (
    <>
      <div
        className={`${greyOutCard ? 'text-gray-200 italic' : 'text-white'} bg-[#2c2c2c] dark:bg-black rounded-t-md sm:max-w-xs relative`}
      >
        <Link
          href={{ pathname: buildPostPath(post) }}
          as={postDetailHref}
          passHref
          onClick={onClick}
          className="absolute w-full h-full top-10 z-20"
        />
        <div className="grid grid-cols-6 justify-between items-center">
          <div className="h-[20px] rounded-t-md col-span-6 flex" />
          <>
            <div className="postcard-gradient absolute top-0 left-0 w-full z-10 h-full rounded-t-md">
              <span className="col-span-5 flex items-center pl-2 mt-2">
                {post.networks
                  ? (
                    <MatchHeader
                      account={post.account}
                      networks={post.networks}
                      matchCount={post.matchCount || 0}
                    />
                  )
                  : (
                    <PostAccount
                      size="medium"
                      post={post}
                      noLink={disableLinks}
                      linkToProfile
                      iconColor="#ffffff"
                    />
                  )}
              </span>
              {!disableLinks && (
                <span className="z-50 absolute top-0 right-0 mt-1">
                  <PostCardMenu
                    post={post}
                    showPin={showPin}
                    fetchData={fetchData}
                    isProfileOwner={isProfileOwner}
                    projectId={projectId}
                  />
                </span>
              )}
            </div>
          </>
        </div>
        <div className="relative text-black dark:text-white">
          {showPin && (
            <PinPost
              postId={post.id}
              isProfileOwner={isProfileOwner}
              onSuccess={fetchData}
            />
          )}
          {!!upperRightOverlay && (
            <div className="absolute top-1 right-1">{upperRightOverlay}</div>
          )}
          <div className="absolute top-3 right-1 flex flex-col gap-1">
            {showFavorite && (
              <MobileTooltip title={`${isFavorited ? 'Remove post from' : 'Add post to'} favorites`}>
                <button type="button" className="z-20" aria-label="favorite-post" onClick={handleFavoritePost}>
                  {isFavorited ? <StarIcon color="primary" fontSize="large" /> : <StarTwoToneIcon color="primary" fontSize="large" />}
                </button>
              </MobileTooltip>
            )}
            {projectListId && (
              <MobileTooltip title="Remove from project">
                <button type="button" className="z-20" aria-label="remove post from project" onClick={removePostFromProject}>
                  <RemoveCircleOutlineIcon color="info" fontSize="large" />
                </button>
              </MobileTooltip>
            )}
          </div>
          <PostCardMedia
            key={post.id}
            post={post}
            className={mediaClassName}
            fixedSize={fixedSize}
            greyOutCard={greyOutCard}
            newPostCard
          />
        </div>
      </div>
      <div className="bg-[#2c2c2c] dark:bg-black rounded-b-md text-white relative z-30">
        <div>
          <CreditsPreview
            postId={post.id}
            credits={post.credits}
            path={buildPostPath(post)}
            onIgnoreSuggestedCredit={onIgnoreSuggestedCredit}
            isProfileOwner={isProfileOwner}
            disableLinks={disableLinks}
            newPostCard
          />
          {showMatchMetrics ? (
            <MatchMetrics
              likes={post.matchLikes}
              shares={post.matchShares}
              combinedViews={post.matchCombinedViews}
              comments={post.matchComments}
              additionalStyling="pb-2"
            />
          ) : (
            <PostMetrics post={post} decimals={0} isPostCard />
          )}
        </div>
      </div>
    </>
  );
};
